import React, { memo, ReactNode, useEffect } from 'react';

import Modal from '@plasma/ui.layout.modal';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';

import EditableTable from '../../components/edit-table';
import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import TopBarThemed from '../../components/top-bar-themed';
import allRfqMasterColumns from '../../types/allRfqMasterDatatype';
import useCurrentRfqStatusHook from './current-rfq-status.hook';
import * as styled from './current-rfq-status.styles';
import themes from './themes';

export interface CurrentRfqStatusProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = CurrentRfqStatusProps & ThemeOverride;

function CurrentRfqStatus(props: Props) {
  const { children, className, style, size, themeMode } = props;

  const {
    t,
    dataForColumns,
    formattedData,
    setFormattedData,
    dataForRFQ,
    columnsForRFQ,
    modalVisible,
    showRFQModal,
    handleRowSelection,
    handleModalViewRFQ,
    handleModalCancel,
    handleRFQModalCancel,
    handleRFQEmailSend,
    statusCodes,
    pageLoading,
    iFrameSrc,
    pdfBlob,
    reportName,
  } = useCurrentRfqStatusHook(props);

  useEffect(() => {
    const formattedData = dataForColumns.map((dt) => {
      const statusColor = statusCodes?.find((sc) => sc.statuscode === dt.status);
      const colorCode = statusColor?.colorcode || 'black';

      const updatedDt: Record<string, React.ReactNode> = {};
      for (const key in dt) {
        if (dt.hasOwnProperty(key)) {
          updatedDt[key] = <div style={{ color: colorCode }}>{dt[key]}</div>;
        }
      }
      return updatedDt;
    });
    setFormattedData(formattedData);
  }, [dataForColumns, statusCodes]);

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.CurrentRfqStatus className={`current-rfq-status ${className ?? ''}`.trim()} style={{ maxHeight: '88vh' }}>
        {pageLoading && <LoadingComponent />}

        <TopBarThemed topHeading={t('current-rfq-status')} />

        {formattedData?.length ? (
          <EditableTable
            tableData={formattedData}
            tableColumns={allRfqMasterColumns}
            Table_Header=""
            hiddenColumns={['status']}
            showActions_ext={false}
            allowSelection
            saveReturnType={'row'}
            stillHiddenColumns={[]}
            handleSelection={handleRowSelection}
            onlyAddableFields={[]}
            allowInsertInitialBlankRow={false}
            clearTheSelection
          />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh',
              fontSize: '30px',
              background: 'linear-gradient(to right, #ff0000, #0000ff, #ffff00)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {pageLoading ? t('') : t('no-record-found')}
          </div>
        )}

        <Modal
          title={`${t('rfq-details-for')} ${dataForRFQ[0]?.rfqCode}`}
          width={'80%'}
          okText={t('view-rfq')}
          confirmLoading={pageLoading}
          cancelText={t('cancel')}
          visible={modalVisible}
          onCancel={() => handleModalCancel()}
          onOk={() => handleModalViewRFQ()}
        >
          <div
            style={{
              display: 'flex',
              borderRadius: '5px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '15px 20px 20px 20px',
              background: 'linear-gradient(to top left, #f0f0f5, #33adff34)',
            }}
          >
            <div style={{ width: '100%', maxHeight: '50vh', overflowY: 'auto' }}>
              <EditableTable
                tableData={dataForRFQ}
                tableColumns={columnsForRFQ}
                Table_Header=""
                hiddenColumns={['status']}
                showActions_ext={false}
                allowSelection={false}
                saveReturnType={'row'}
                stillHiddenColumns={[]}
                onlyAddableFields={[]}
                allowInsertInitialBlankRow={false}
              />
            </div>
          </div>
        </Modal>

        <Modal
          title={`${t('rfq-details-for')} ${dataForRFQ[0]?.rfqCode}`}
          width={'82%'}
          okText={t('email-rfq')}
          cancelText={t('cancel')}
          confirmLoading={pageLoading}
          visible={showRFQModal}
          onCancel={() => handleRFQModalCancel()}
          onOk={() => handleRFQEmailSend()}
        >
          <iframe src={iFrameSrc} title="PDF" style={{ marginTop: '1vh', height: '70vh', width: '100%' }} />
        </Modal>
      </styled.CurrentRfqStatus>
    </ThemeProvider>
  );
}

export default memo(CurrentRfqStatus) as unknown as typeof CurrentRfqStatus;
